import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Star,
  Loader2,
  X,
  Home,
  MapPin,
  Calendar,
  Upload,
  Trash,
  GripVertical,
} from "lucide-react";
import { getLogements, createLogement, updateLogement, deleteLogement, toggleCoupDeCoeur, getProprietaires } from "@/lib/api";
import apiClient from "@/lib/api";
import ICalSyncPanel from "@/components/ICalSyncPanel";

const APARTMENT_IMAGES = [
  "https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg",
  "https://images.unsplash.com/photo-1760067537740-faa11f7bdf1e?crop=entropy&cs=srgb&fm=jpg",
  "https://images.unsplash.com/photo-1760067537116-de1f76fe8f95?crop=entropy&cs=srgb&fm=jpg"
];

export default function LogementsAdminPage() {
  const [logements, setLogements] = useState([]);
  const [proprietaires, setProprietaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLogement, setEditingLogement] = useState(null);
  const [icalLogement, setIcalLogement] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingPhoto(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const formPayload = new FormData();
        formPayload.append("file", file);
        const res = await apiClient.post("/upload", formPayload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
        uploaded.push(url);
      } catch {
        toast.error(`Erreur upload : ${file.name}`);
      }
    }
    if (uploaded.length) {
      setFormData(f => ({ ...f, photos: [...(f.photos || []), ...uploaded] }));
      toast.success(`${uploaded.length} photo(s) ajoutée(s) !`);
    }
    setUploadingPhoto(false);
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const removePhoto = (index) => {
    setFormData(f => ({ ...f, photos: f.photos.filter((_, i) => i !== index) }));
  };

  const [dragIndex, setDragIndex] = useState(null);

  const handleDragStart = (i) => setDragIndex(i);
  const handleDragOver = (e, i) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) return;
    setFormData(f => {
      const photos = [...f.photos];
      const dragged = photos.splice(dragIndex, 1)[0];
      photos.splice(i, 0, dragged);
      return { ...f, photos };
    });
    setDragIndex(i);
  };
  const handleDragEnd = () => setDragIndex(null);

  // ── Drag & drop pour réordonner les annonces ──────────────────────
  const [listDragIndex, setListDragIndex] = useState(null);
  const [listDragOver, setListDragOver] = useState(null);

  const handleListDragStart = (i) => setListDragIndex(i);
  const handleListDragOver = (e, i) => {
    e.preventDefault();
    setListDragOver(i);
  };
  const handleListDrop = (e, i) => {
    e.preventDefault();
    if (listDragIndex === null || listDragIndex === i) {
      setListDragIndex(null);
      setListDragOver(null);
      return;
    }
    const newOrder = [...logements];
    const dragged = newOrder.splice(listDragIndex, 1)[0];
    newOrder.splice(i, 0, dragged);
    setLogements(newOrder);
    setListDragIndex(null);
    setListDragOver(null);
    toast.success("Ordre mis à jour !");
  };
  const handleListDragEnd = () => { setListDragIndex(null); setListDragOver(null); };
  const [formData, setFormData] = useState({
    titre: "",
    description: "",
    ville: "Limoges",
    adresse: "",
    type_logement: "appartement",
    capacite: 2,
    prix_nuit: 50,
    equipements: [],
    photos: [],
    coup_de_coeur: false,
    proprietaire_id: "",
    livret_accueil: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [logementsRes, proprietairesRes] = await Promise.all([
        getLogements(),
        getProprietaires()
      ]);
      setLogements(logementsRes.data);
      setProprietaires(proprietairesRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingLogement) {
        await updateLogement(editingLogement.id, formData);
        toast.success("Logement mis à jour !");
      } else {
        await createLogement(formData);
        toast.success("Logement créé !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (logement) => {
    setEditingLogement(logement);
    setFormData({
      titre: logement.titre,
      description: logement.description,
      ville: logement.ville,
      adresse: logement.adresse,
      type_logement: logement.type_logement,
      capacite: logement.capacite,
      prix_nuit: logement.prix_nuit,
      equipements: logement.equipements || [],
      photos: logement.photos || [],
      coup_de_coeur: logement.coup_de_coeur,
      proprietaire_id: logement.proprietaire_id || "",
      livret_accueil: logement.livret_accueil || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce logement ?")) return;
    try {
      await deleteLogement(id);
      toast.success("Logement supprimé !");
      loadData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleToggleCoupDeCoeur = async (id) => {
    try {
      await toggleCoupDeCoeur(id);
      loadData();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const resetForm = () => {
    setEditingLogement(null);
    setFormData({
      titre: "",
      description: "",
      ville: "Limoges",
      adresse: "",
      type_logement: "appartement",
      capacite: 2,
      prix_nuit: 50,
      equipements: [],
      photos: [],
      coup_de_coeur: false,
      proprietaire_id: "",
      livret_accueil: ""
    });
  };

  const handleEquipementChange = (eq) => {
    const current = formData.equipements || [];
    if (current.includes(eq)) {
      setFormData({ ...formData, equipements: current.filter(e => e !== eq) });
    } else {
      setFormData({ ...formData, equipements: [...current, eq] });
    }
  };

  const getImage = (logement, index) => {
    if (logement.photos && logement.photos.length > 0) {
      return logement.photos[0];
    }
    return APARTMENT_IMAGES[index % APARTMENT_IMAGES.length];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#004333]" />
      </div>
    );
  }

  return (
    <div data-testid="logements-admin-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-display text-[#004333]">Gestion des logements</h2>
          <p className="text-gray-500">{logements.length} logement(s)</p>
        </div>
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="btn-primary flex items-center gap-2"
          data-testid="add-logement-btn"
        >
          <Plus className="w-4 h-4" />
          Ajouter un logement
        </button>
      </div>

      {logements.length > 0 ? (
        <>
          <p className="text-xs text-gray-400 mb-4 flex items-center gap-1">
            <GripVertical className="w-3.5 h-3.5" /> Glisser les cartes pour changer l'ordre d'affichage sur le site
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {logements.map((logement, index) => (
            <div
              key={logement.id}
              draggable
              onDragStart={() => handleListDragStart(index)}
              onDragOver={(e) => handleListDragOver(e, index)}
              onDrop={(e) => handleListDrop(e, index)}
              onDragEnd={handleListDragEnd}
              style={{ userSelect: 'none' }}
              className={`bg-white border-2 overflow-hidden transition-all duration-150 cursor-grab active:cursor-grabbing rounded-xl ${
                listDragIndex === index
                  ? "border-[#C9922A] opacity-50 scale-95"
                  : listDragOver === index
                  ? "border-[#C9922A] shadow-lg scale-[1.02]"
                  : "border-gray-200 hover:border-gray-300"
              }`}>
              <div className="aspect-video relative">
                {/* Grip handle */}
                <div className="absolute top-2 left-2 z-10 bg-black/40 backdrop-blur-sm rounded-lg p-1.5 text-white/80">
                  <GripVertical className="w-4 h-4" />
                </div>
                {/* Position badge */}
                <div className="absolute top-2 left-10 z-10 bg-black/40 backdrop-blur-sm rounded-lg px-2 py-1 text-white/80 text-xs font-medium">
                  #{index + 1}
                </div>
                <img
                  src={getImage(logement, index)}
                  alt={logement.titre}
                  className="w-full h-full object-cover pointer-events-none"
                />
                <div className="absolute top-2 right-2 flex gap-2">
                  <button
                    onClick={() => handleToggleCoupDeCoeur(logement.id)}
                    className={`w-8 h-8 flex items-center justify-center ${
                      logement.coup_de_coeur ? "bg-[#C9922A] text-white" : "bg-white text-gray-400"
                    }`}
                    data-testid={`toggle-coup-coeur-${logement.id}`}
                  >
                    <Star className="w-4 h-4" fill={logement.coup_de_coeur ? "currentColor" : "none"} />
                  </button>
                </div>
                <div className="absolute bottom-2 left-2">
                  <span className={`text-xs font-medium px-2 py-1 ${
                    logement.statut === "actif" ? "bg-green-500 text-white" : "bg-gray-500 text-white"
                  }`}>
                    {logement.statut}
                  </span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-display text-lg text-[#004333] mb-1">{logement.titre}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                  <MapPin className="w-3 h-3" />
                  {logement.ville}
                  <span>•</span>
                  <Home className="w-3 h-3" />
                  {logement.type_logement}
                  <span>•</span>
                  {logement.capacite} pers.
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-mono font-bold text-[#004333]">{logement.prix_nuit} €/nuit</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIcalLogement(logement)}
                      className="p-2 text-gray-400 hover:text-[#C9922A] transition-colors"
                      title="Synchronisation iCal"
                      data-testid={`ical-logement-${logement.id}`}
                    >
                      <Calendar className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(logement)}
                      className="p-2 text-gray-400 hover:text-[#004333] transition-colors"
                      data-testid={`edit-logement-${logement.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(logement.id)}
                      className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                      data-testid={`delete-logement-${logement.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
      ) : (
        <div className="bg-white border border-gray-200 p-16 text-center">
          <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="font-display text-xl text-[#004333] mb-2">Aucun logement</h3>
          <p className="text-gray-500 mb-6">Commencez par ajouter votre premier logement.</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="btn-primary"
          >
            Ajouter un logement
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-display text-xl text-[#004333]">
                {editingLogement ? "Modifier le logement" : "Nouveau logement"}
              </h2>
              <button onClick={() => setShowModal(false)}>
                <X className="w-6 h-6 text-gray-400 hover:text-gray-600" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
                <input
                  type="text"
                  required
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  className="input-hh"
                  placeholder="Ex: Appartement cosy centre-ville"
                  data-testid="logement-titre"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                <textarea
                  rows={4}
                  required
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-hh"
                  data-testid="logement-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ville</label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                    className="input-hh"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Adresse *</label>
                  <input
                    type="text"
                    required
                    value={formData.adresse}
                    onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    className="input-hh"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                  <select
                    value={formData.type_logement}
                    onChange={(e) => setFormData({ ...formData, type_logement: e.target.value })}
                    className="input-hh"
                  >
                    <option value="studio">Studio</option>
                    <option value="appartement">Appartement</option>
                    <option value="maison">Maison</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Capacité</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.capacite}
                    onChange={(e) => setFormData({ ...formData, capacite: parseInt(e.target.value) })}
                    className="input-hh"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Prix/nuit (€)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.prix_nuit}
                    onChange={(e) => setFormData({ ...formData, prix_nuit: parseFloat(e.target.value) })}
                    className="input-hh"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Équipements</label>
                <div className="flex flex-wrap gap-2">
                  {["Wifi", "Parking", "TV", "Cuisine", "Climatisation", "Lave-linge", "Sèche-cheveux"].map((eq) => (
                    <button
                      key={eq}
                      type="button"
                      onClick={() => handleEquipementChange(eq)}
                      className={`px-3 py-1 text-sm border transition-colors ${
                        formData.equipements?.includes(eq)
                          ? "bg-[#004333] text-white border-[#004333]"
                          : "bg-white text-gray-600 border-gray-200 hover:border-[#004333]"
                      }`}
                    >
                      {eq}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Propriétaire (optionnel)</label>
                <select
                  value={formData.proprietaire_id}
                  onChange={(e) => setFormData({ ...formData, proprietaire_id: e.target.value })}
                  className="input-hh"
                >
                  <option value="">Aucun (bien propre)</option>
                  {proprietaires.map((prop) => (
                    <option key={prop.id} value={prop.id}>{prop.nom}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Photos du logement
                  <span className="text-xs text-gray-400 font-normal ml-2">JPEG, PNG, WebP — max 5 Mo par photo</span>
                </label>

                {/* Upload button */}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed border-[#C9922A]/40 rounded-xl text-[#C9922A] font-medium text-sm hover:bg-[#C9922A]/5 hover:border-[#C9922A]/70 transition-all disabled:opacity-50 mb-3"
                >
                  {uploadingPhoto
                    ? <Loader2 className="w-5 h-5 animate-spin" />
                    : <Upload className="w-5 h-5" />
                  }
                  {uploadingPhoto ? "Upload en cours..." : "Cliquer pour ajouter des photos"}
                </button>

                {/* Photo previews — drag & drop */}
                {formData.photos?.length > 0 && (
                  <>
                    <p className="text-xs text-gray-400 mb-2 flex items-center gap-1">
                      <span>↕</span> Glisser-déposer pour réordonner — la 1ère photo est la couverture
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {formData.photos.map((url, i) => (
                        <div
                          key={url + i}
                          draggable
                          onDragStart={() => handleDragStart(i)}
                          onDragOver={(e) => handleDragOver(e, i)}
                          onDragEnd={handleDragEnd}
                          className={`relative group rounded-xl overflow-hidden border-2 aspect-square cursor-grab active:cursor-grabbing transition-all duration-150 ${
                            dragIndex === i
                              ? "border-[#C9922A] opacity-50 scale-95"
                              : "border-gray-200 hover:border-[#C9922A]/50"
                          }`}
                        >
                          <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />

                          {/* Overlay au survol */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-end justify-between p-1.5 opacity-0 group-hover:opacity-100">
                            <span className="text-xs text-white bg-black/60 px-1.5 py-0.5 rounded font-medium">
                              #{i + 1}
                            </span>
                            <button
                              type="button"
                              onClick={() => removePhoto(i)}
                              className="p-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                              title="Supprimer"
                            >
                              <Trash className="w-3 h-3" />
                            </button>
                          </div>

                          {/* Badge couverture */}
                          {i === 0 && (
                            <div className="absolute top-1 left-1 bg-[#C9922A] text-white text-xs px-1.5 py-0.5 rounded-full font-medium pointer-events-none">
                              Couverture
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {(!formData.photos || formData.photos.length === 0) && (
                  <p className="text-xs text-gray-400 text-center py-2">Aucune photo ajoutée</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Livret d'accueil (HTML ou texte)</label>
                <textarea
                  rows={3}
                  value={formData.livret_accueil}
                  onChange={(e) => setFormData({ ...formData, livret_accueil: e.target.value })}
                  className="input-hh"
                  placeholder="Instructions d'accès, wifi, etc."
                />
              </div>

              <div className="flex items-center gap-3 p-4 border border-gray-200 bg-[#FAFAF8]">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, coup_de_coeur: !formData.coup_de_coeur })}
                  className={`w-10 h-10 flex items-center justify-center transition-colors ${
                    formData.coup_de_coeur ? "bg-[#C9922A] text-white" : "bg-white text-gray-400 border border-gray-200"
                  }`}
                  data-testid="form-coup-de-coeur-toggle"
                >
                  <Star className="w-5 h-5" fill={formData.coup_de_coeur ? "currentColor" : "none"} />
                </button>
                <div>
                  <span className="text-sm font-medium text-[#004333]">Coup de cœur</span>
                  <p className="text-xs text-gray-500">Ce bien sera mis en avant sur la page d'accueil</p>
                </div>
              </div>
              
              <div className="flex gap-4 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">
                  Annuler
                </button>
                <button type="submit" className="btn-primary flex-1" data-testid="logement-submit">
                  {editingLogement ? "Mettre à jour" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* iCal Sync Modal */}
      {icalLogement && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="ical-modal">
          <div className="bg-white rounded-md max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-semibold text-[#004333]">Synchro iCal</h2>
                <p className="text-sm text-gray-400">{icalLogement.titre}</p>
              </div>
              <button
                onClick={() => setIcalLogement(null)}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                data-testid="ical-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <ICalSyncPanel logementId={icalLogement.id} logementTitre={icalLogement.titre} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
